import { useEffect, useState } from 'react';
import { FiCheck, FiEdit2, FiEye, FiEyeOff, FiPlus, FiTrash2 } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { adminApi, type AdminPlan } from '../../services/admin';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';

const ALL_FEATURES = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'graficos', label: 'Gráficos' },
    { key: 'accounts', label: 'Contas' },
    { key: 'transactions', label: 'Transações' },
    { key: 'cashflow', label: 'Fluxo de Caixa' },
    { key: 'reports', label: 'Relatórios' },
    // { key: 'budget', label: 'Orçamento' },              // sem equivalência
    // { key: 'recurring', label: 'Recorrentes' },           // sem equivalência
    // { key: 'goals', label: 'Metas' },                     // sem equivalência
    // { key: 'investments', label: 'Investimentos' },       // sem equivalência
    { key: 'settings', label: 'Configurações' },
    // { key: 'export_pdf', label: 'Exportar PDF' },         // sem equivalência
    // { key: 'export_excel', label: 'Exportar Excel' },     // sem equivalência
    // { key: 'multiple_accounts', label: 'Múltiplas Contas' },      // sem equivalência
    // { key: 'custom_categories', label: 'Categorias Personalizadas' }, // sem equivalência
    // { key: 'api_access', label: 'Acesso à API' },         // sem equivalência
    // { key: 'priority_support', label: 'Suporte Prioritário' },    // sem equivalência
    { key: 'import', label: 'Importação de Planilha' },
    { key: 'filiais', label: 'Múltiplos Negócios (Filiais)' },
];

type PlanForm = { name: string; description: string; price: string; interval: 'monthly' | 'yearly'; maxAccounts: string; maxTransactionsPerMonth: string; features: string[] };
const emptyForm = (): PlanForm => ({ name: '', description: '', price: '0', interval: 'monthly', maxAccounts: '5', maxTransactionsPerMonth: '100', features: ['dashboard', 'settings'] });

function slugify(input: string): string {
    return input
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export function AdminPlans() {
    const [plans, setPlans] = useState<AdminPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<AdminPlan | null>(null);
    const [form, setForm] = useState<PlanForm>(emptyForm());
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<AdminPlan | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try { setPlans(await adminApi.listPlans()); }
        catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(emptyForm()); setFormError(null); setModalOpen(true); };
    const openEdit = (p: AdminPlan) => {
        setEditing(p);
        setForm({ name: p.name, description: p.description, price: String(p.price), interval: p.interval ?? 'monthly', maxAccounts: String(p.maxAccounts), maxTransactionsPerMonth: String(p.maxTransactionsPerMonth), features: [...p.features] });
        setFormError(null);
        setModalOpen(true);
    };

    const toggleFeature = (key: string) => {
        setForm(prev => ({ ...prev, features: prev.features.includes(key) ? prev.features.filter(f => f !== key) : [...prev.features, key] }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { name: form.name, description: form.description, price: parseFloat(form.price), interval: form.interval, maxAccounts: parseInt(form.maxAccounts), maxTransactionsPerMonth: parseInt(form.maxTransactionsPerMonth), features: form.features };
            if (editing) {
                await adminApi.updatePlan(editing.id, payload);
            } else {
                await adminApi.createPlan(payload);
            }
            setModalOpen(false);
            load();
        } catch (err: any) { setFormError(err.message); }
        finally { setSaving(false); }
    };

    const toggleActive = async (p: AdminPlan) => {
        await adminApi.updatePlan(p.id, { isActive: !p.isActive });
        load();
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await adminApi.deletePlan(deleting.id);
            setDeleting(null);
            load();
        } catch (err: any) {
            setDeleteError(err.message);
        } finally {
            setDeleteBusy(false);
        }
    };

    const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Planos</h2>
                    <p>Configure os planos de assinatura da plataforma</p>
                </div>
                <Button onClick={openNew}><FiPlus size={16} style={{ marginRight: '0.5rem' }} />Novo Plano</Button>
            </div>

            {loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : (
                <div className={styles.plansGrid}>
                    {plans.map(plan => (
                        <div key={plan.id} className={styles.planCard}>
                            <div className={styles.planHeader}>
                                <div className={styles.planInfo}>
                                    <h3>{plan.name}</h3>
                                    <p>{plan.description}</p>
                                    {plan.externalRef && (
                                        <code style={{ display: 'inline-block', marginTop: 4, fontSize: 11, padding: '2px 6px', background: 'rgba(0,0,0,0.06)', borderRadius: 4 }}>
                                            ref: {plan.externalRef}
                                        </code>
                                    )}
                                </div>
                                <div className={styles.planPrice}>
                                    <div className={styles.amount}>{plan.price === 0 ? 'Grátis' : formatCurrency(plan.price)}</div>
                                    {plan.price > 0 && <div className={styles.period}>{plan.interval === 'yearly' ? '/ano' : '/mês'}</div>}
                                </div>
                            </div>
                            <div className={styles.planBody}>
                                <div className={styles.planLimits}>
                                    <div className={styles.limitItem}><div className={styles.label}>Negócios</div><div className={styles.value}>{plan.maxAccounts}</div></div>
                                    <div className={styles.limitItem}><div className={styles.label}>Transações/mês</div><div className={styles.value}>{plan.maxTransactionsPerMonth === -1 ? 'Ilimitado' : plan.maxTransactionsPerMonth}</div></div>
                                </div>
                                <div className={styles.featuresSection}>
                                    <h4>Funcionalidades ({plan.features.length})</h4>
                                    <div className={styles.featuresList}>
                                        {plan.features.slice(0, 6).map(f => (
                                            <span key={f} className={styles.featureTag}><FiCheck size={10} />{ALL_FEATURES.find(x => x.key === f)?.label ?? f}</span>
                                        ))}
                                        {plan.features.length > 6 && <span className={styles.featureTag}>+{plan.features.length - 6} mais</span>}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.planFooter}>
                                <button className={`${styles.planBtn} ${styles.edit}`} onClick={() => openEdit(plan)}>
                                    <FiEdit2 size={14} /> Editar
                                </button>
                                <button className={`${styles.planBtn} ${styles.toggle} ${!plan.isActive ? styles.inactive : ''}`} onClick={() => toggleActive(plan)}>
                                    {plan.isActive ? <><FiEye size={14} /> Ativo</> : <><FiEyeOff size={14} /> Inativo</>}
                                </button>
                                <button className={`${styles.planBtn} ${styles.delete}`} onClick={() => { setDeleteError(null); setDeleting(plan); }}>
                                    <FiTrash2 size={14} /> Excluir
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setFormError(null); }} title={editing ? `Editar: ${editing.name}` : 'Novo Plano'}>
                <form onSubmit={handleSubmit}>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <Input label="Nome do Plano" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Profissional" />
                            {form.name && (
                                <small style={{ display: 'block', marginTop: 4, fontSize: 11, color: 'var(--text-secondary, #666)' }}>
                                    Ref para PlugAndPay: <code>{editing?.externalRef ?? slugify(form.name)}</code>
                                </small>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel} style={{ marginBottom: 3 }}>Periodicidade</label>
                            <select
                                className={styles.formSelect}
                                value={form.interval}
                                onChange={e => setForm(p => ({ ...p, interval: e.target.value as 'monthly' | 'yearly' }))}
                            >
                                <option value="monthly">Mensal</option>
                                <option value="yearly">Anual</option>
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <Input
                                label={form.interval === 'yearly' ? 'Preço Anual total (R$)' : 'Preço Mensal (R$)'}
                                type="number"
                                value={form.price}
                                onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                                placeholder="0.00"
                            />
                            {form.interval === 'yearly' && (
                                <small style={{ display: 'block', marginTop: 4, fontSize: 11, color: 'var(--text-secondary, #666)' }}>
                                    Informe o valor TOTAL cobrado uma vez por ano — o MRR já é calculado dividindo por 12.
                                </small>
                            )}
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <Input label="Descrição" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Descrição do plano..." />
                        </div>
                        <div className={styles.formGroup}>
                            <Input label="Máximo de negócios (matriz + filiais)" type="number" min={1} value={form.maxAccounts} onChange={e => setForm(p => ({ ...p, maxAccounts: e.target.value }))} placeholder="5" />
                        </div>
                        <div className={styles.formGroup}>
                            <Input label="Transações por Mês" type="number" min={0} value={form.maxTransactionsPerMonth} onChange={e => setForm(p => ({ ...p, maxTransactionsPerMonth: e.target.value }))} placeholder="Ex: 100" />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label className={styles.formLabel}>Funcionalidades</label>
                            <div className={styles.featureCheckboxGrid}>
                                {ALL_FEATURES.map(f => (
                                    <label key={f.key} className={styles.featureCheckbox}>
                                        <input type="checkbox" checked={form.features.includes(f.key)} onChange={() => toggleFeature(f.key)} />
                                        {f.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    {formError && <p className={styles.formError}>{formError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setModalOpen(false); setFormError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar Plano'}</Button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={!!deleting} onClose={() => { setDeleting(null); setDeleteError(null); }} title="Excluir Plano">
                <div style={{ padding: '0.5rem 0 1rem' }}>
                    <p>Tem certeza que deseja excluir o plano <strong>{deleting?.name}</strong>?</p>
                    <p style={{ marginTop: '0.5rem', fontSize: 13, color: 'var(--text-secondary, #666)' }}>Esta ação é irreversível. Planos com clientes ou assinaturas vinculadas não podem ser excluídos.</p>
                    {deleteError && <p className={styles.formError}>{deleteError}</p>}
                </div>
                <div className={styles.modalFooter}>
                    <Button type="button" onClick={() => { setDeleting(null); setDeleteError(null); }}>Cancelar</Button>
                    <Button type="button" disabled={deleteBusy} onClick={confirmDelete}>
                        {deleteBusy ? 'Excluindo...' : 'Confirmar Exclusão'}
                    </Button>
                </div>
            </Modal>
        </AdminLayout>
    );
}
